import { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import {
  Icon,
  Tooltip,
  DropdownMenu,
  type DropdownMenuItemOrDivider,
} from '../';
import type { NavBarProps, NavBarAction, NavBarIconType } from './NavBar.types';

/**
 * Helper to determine if an icon is a string (Material Icon name) or a React element
 */
function isStringIcon(icon: NavBarIconType): icon is string {
  return typeof icon === 'string';
}

/**
 * Render an icon based on its type
 * @param icon - Either a Material Symbol name string or a React element
 * @param size - Size in pixels for Material Symbols (ignored for React elements)
 */
function renderIcon(icon: NavBarIconType, size: number = 20): React.ReactNode {
  if (isStringIcon(icon)) {
    return <Icon name={icon} size={size} />;
  }
  // Return the React element as-is (e.g., custom SVG)
  return icon;
}

/**
 * Styled components using Emotion with theme tokens
 */
const NavContainer = styled.nav<{ $variant: 'light' | 'dark'; $sticky: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  min-width: 320px;
  padding: 0 ${({ theme }) => theme.spacing[4]};
  box-sizing: border-box;
  font-family: ${({ theme }) => theme.typography.fontFamily};

  /* Light variant - uses primary colors from theme */
  background-color: ${({ theme, $variant }) =>
    $variant === 'light' ? theme.colors.primary : theme.colors.grey[900]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  color: ${({ theme, $variant }) =>
    $variant === 'light' ? theme.colors.primaryText : theme.colors.white};

  /* Sticky positioning */
  ${({ $sticky }) =>
    $sticky &&
    `
    position: sticky;
    top: 0;
    z-index: 1000;
  `}
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  min-width: 0;
  flex-shrink: 0;
`;

const LogoContainer = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  height: 34px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    opacity: ${({ $clickable }) => ($clickable ? 0.8 : 1)};
  }
`;

const Logo = styled.img`
  height: 100%;
  width: auto;
  max-width: 200px;
  object-fit: contain;
`;

const Title = styled.span<{ $variant: 'light' | 'dark' }>`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  color: ${({ theme, $variant }) =>
    $variant === 'light' ? theme.colors.primaryText : theme.colors.white};
`;

const CenterSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 0 ${({ theme }) => theme.spacing[4]};
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  flex-shrink: 0;
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const ActionButton = styled.button<{ $variant: 'light' | 'dark' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  background: transparent;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  /* Icon color based on variant */
  color: ${({ theme, $variant }) =>
    $variant === 'light' ? theme.colors.primaryText : theme.colors.grey[300]};

  &:hover:not(:disabled) {
    /* Only border highlight on hover, no background fill */
    border-color: ${({ theme, $variant }) =>
      $variant === 'light' ? theme.colors.primaryText : theme.colors.grey[300]};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlight};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Menu trigger button - matches ActionButton styling for consistency
 */
const MenuTriggerButton = styled.button<{ $variant: 'light' | 'dark' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  background: transparent;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  /* Icon color based on variant */
  color: ${({ theme, $variant }) =>
    $variant === 'light' ? theme.colors.primaryText : theme.colors.grey[300]};

  &:hover:not(:disabled) {
    /* Only border highlight on hover, no background fill */
    border-color: ${({ theme, $variant }) =>
      $variant === 'light' ? theme.colors.primaryText : theme.colors.grey[300]};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlight};
    outline-offset: 2px;
  }
`;

/**
 * Three dots icon for menu trigger
 */
const MoreVertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

/**
 * NavBar component for integration applications
 *
 * Features:
 * - Logo on the left with optional click handler
 * - Optional title text
 * - Action buttons on the right (with SVG or Material Icon support)
 * - Dropdown menu on the far right
 * - Responsive collapse: actions become menu items at breakpoint
 * - Primary color background with primaryText color icons (light variant)
 */
export function NavBar({
  logoSrc,
  logoAlt = 'Logo',
  onLogoClick,
  title,
  actions = [],
  menuItems = [],
  menuLabel = 'Menu',
  dropdownMenuProps,
  collapseBreakpoint = 480,
  variant = 'light',
  sticky = false,
  className,
  children,
  'data-testid': testId = 'navbar',
  dataTestId,
  dataId,
}: NavBarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  // Check if we need to collapse actions
  const checkCollapse = useCallback(() => {
    if (typeof window !== 'undefined') {
      setIsCollapsed(window.innerWidth <= collapseBreakpoint);
    }
  }, [collapseBreakpoint]);

  useEffect(() => {
    checkCollapse();
    window.addEventListener('resize', checkCollapse);
    return () => window.removeEventListener('resize', checkCollapse);
  }, [checkCollapse]);

  // Convert actions to DropdownMenu items when collapsed
  const collapsedActionItems: DropdownMenuItemOrDivider[] = isCollapsed
    ? actions.map((action) => ({
        id: action.id,
        displayText: action.label,
        icon: renderIcon(action.icon, 16),
        onClick: action.onClick,
        disabled: action.disabled,
      }))
    : [];

  // Convert menu items to DropdownMenu items
  const regularMenuItems: DropdownMenuItemOrDivider[] = menuItems.map((item) => ({
    id: item.id,
    displayText: item.label,
    icon: item.icon ? renderIcon(item.icon, 16) : undefined,
    onClick: item.onClick,
    disabled: item.disabled,
  }));

  // Combined menu items with divider between sections
  const allMenuItems: DropdownMenuItemOrDivider[] = [];

  if (collapsedActionItems.length > 0) {
    allMenuItems.push(...collapsedActionItems);
    if (regularMenuItems.length > 0) {
      allMenuItems.push({ type: 'divider' as const });
    }
  }
  allMenuItems.push(...regularMenuItems);

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    }
  };

  const handleLogoKeyDown = (e: React.KeyboardEvent) => {
    if (onLogoClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onLogoClick();
    }
  };

  const renderActionButton = (action: NavBarAction) => (
    <Tooltip key={action.id} content={action.tooltip || action.label}>
      <ActionButton
        type="button"
        $variant={variant}
        onClick={action.onClick}
        disabled={action.disabled}
        aria-label={action.label}
        data-testid={`navbar-action-${action.id}`}
      >
        {renderIcon(action.icon, 20)}
      </ActionButton>
    </Tooltip>
  );

  const hasMenu = allMenuItems.length > 0;

  return (
    <NavContainer
      ref={navRef}
      className={className}
      $variant={variant}
      $sticky={sticky}
      data-testid={dataTestId || testId}
      data-id={dataId}
      data-variant={variant}
      data-sticky={sticky ? 'true' : undefined}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Left section: Logo and title */}
      <LeftSection>
        <LogoContainer
          $clickable={!!onLogoClick}
          onClick={handleLogoClick}
          onKeyDown={handleLogoKeyDown}
          role={onLogoClick ? 'button' : undefined}
          tabIndex={onLogoClick ? 0 : undefined}
          aria-label={onLogoClick ? `${logoAlt} - Click to go home` : undefined}
        >
          <Logo src={logoSrc} alt={logoAlt} />
        </LogoContainer>
        {title && <Title $variant={variant}>{title}</Title>}
      </LeftSection>

      {/* Center section: Children */}
      {children && <CenterSection>{children}</CenterSection>}

      {/* Right section: Actions and menu */}
      <RightSection>
        {/* Regular actions (hidden when collapsed) */}
        {!isCollapsed && actions.length > 0 && (
          <ActionsContainer data-testid="navbar-actions">
            {actions.map(renderActionButton)}
          </ActionsContainer>
        )}

        {/* Dropdown menu with custom trigger matching action button style */}
        {hasMenu && (
          <DropdownMenu
            placement="bottom-end"
            inverse={variant === 'dark'}
            aria-label={menuLabel}
            dataTestId="navbar-menu"
            {...dropdownMenuProps}
            trigger={
              <MenuTriggerButton
                type="button"
                $variant={variant}
                aria-label={menuLabel}
              >
                <MoreVertIcon />
              </MenuTriggerButton>
            }
            items={[...allMenuItems, ...(dropdownMenuProps?.items || [])]}
          />
        )}
      </RightSection>
    </NavContainer>
  );
}
